import type { Expense } from './db'
import { CATEGORIES } from '../pages/record/constants'

export interface OverviewStats {
  totalAmount: number
  totalExcludeCar: number
  minOdometer: number   // 0 = 無資料
  maxOdometer: number
  ridden: number
  costPerKm: number | null       // null = 尚無里程資料
  fuelCostPerKm: number | null
}

export interface CategoryRow {
  category: string
  total: number
  count: number
  ratio: number         // 0–1
  periodTotal: number
}

export interface MonthRow {
  month: string         // 'YYYY-MM'
  byCategory: Record<string, number>
  rowTotal: number
}

/** 取得有效紀錄（未刪除） */
function alive(expenses: Expense[]) {
  return expenses.filter(e => e.deleted_at === null)
}

/** 總覽數值 */
export function calcOverview(
  expenses: Expense[],
  odometerMax: number,   // 來自 getCurrentOdometer()（含 odometer_readings）
): OverviewStats {
  const rows = alive(expenses)

  const totalAmount = rows.reduce((s, e) => s + e.amount, 0)
  const totalExcludeCar = rows
    .filter(e => e.category !== '車錢')
    .reduce((s, e) => s + e.amount, 0)

  const odos = rows
    .map(e => e.odometer_km)
    .filter((v): v is number => v != null && v > 0)

  const minOdometer = odos.length > 0 ? Math.min(...odos) : 0
  const maxOdometer = Math.max(odometerMax, ...(odos.length > 0 ? odos : [0]))
  const ridden = maxOdometer - minOdometer

  const fuelTotal = rows
    .filter(e => e.category === '油錢')
    .reduce((s, e) => s + e.amount, 0)

  return {
    totalAmount,
    totalExcludeCar,
    minOdometer,
    maxOdometer,
    ridden,
    costPerKm: ridden > 0 ? totalExcludeCar / ridden : null,
    fuelCostPerKm: ridden > 0 ? fuelTotal / ridden : null,
  }
}

/** 類別表 */
export function calcCategoryRows(
  expenses: Expense[],
  statsStartMonth: string | null,  // 'YYYY-MM-DD'
): CategoryRow[] {
  const rows = alive(expenses)
  const totalAmount = rows.reduce((s, e) => s + e.amount, 0)

  // 統計期間：起始月起 12 個月
  let periodFrom = ''
  let periodTo = ''
  if (statsStartMonth) {
    periodFrom = statsStartMonth.slice(0, 7) + '-01'
    const d = new Date(periodFrom)
    d.setMonth(d.getMonth() + 12)
    periodTo = d.toISOString().slice(0, 10)
  }

  return CATEGORIES.map(cat => {
    const catRows = rows.filter(e => e.category === cat)
    const total = catRows.reduce((s, e) => s + e.amount, 0)
    const count = catRows.length
    const ratio = totalAmount > 0 ? total / totalAmount : 0

    const periodTotal = statsStartMonth
      ? catRows
          .filter(e => e.date >= periodFrom && e.date < periodTo)
          .reduce((s, e) => s + e.amount, 0)
      : 0

    return { category: cat, total, count, ratio, periodTotal }
  })
}

/** 每月花費表（12 個月） */
export function calcMonthRows(
  expenses: Expense[],
  statsStartMonth: string | null,
): MonthRow[] {
  if (!statsStartMonth) return []

  const rows = alive(expenses)
  const start = new Date(statsStartMonth.slice(0, 7) + '-01')
  const months: MonthRow[] = []

  for (let i = 0; i < 12; i++) {
    const d = new Date(start)
    d.setMonth(d.getMonth() + i)
    const month = d.toISOString().slice(0, 7)  // 'YYYY-MM'

    const byCategory: Record<string, number> = {}
    for (const cat of CATEGORIES) byCategory[cat] = 0

    for (const e of rows) {
      if (e.date.startsWith(month)) {
        byCategory[e.category] = (byCategory[e.category] ?? 0) + e.amount
      }
    }

    const rowTotal = Object.values(byCategory).reduce((s, v) => s + v, 0)
    months.push({ month, byCategory, rowTotal })
  }

  return months
}
